import React from 'react';
import { createPortal } from 'react-dom';
import { FmButton } from './FmButton';
import { FmOverlayPortalContext } from './FmOverlayPortalContext';

type TriggerProps = React.HTMLAttributes<HTMLElement> & {
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup'];
  'aria-expanded'?: React.AriaAttributes['aria-expanded'];
};

export type FmConfirmPlacement = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

export interface FmConfirmationAffectedEntityDescriptor {
  id: string;
  label: string;
  outcome: string;
}

/** Page-neutral scalar contract for a mutation that requires explicit consent. */
export interface FmActionConfirmationDescriptor {
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  affectedEntities: readonly [
    FmConfirmationAffectedEntityDescriptor,
    ...FmConfirmationAffectedEntityDescriptor[],
  ];
}

export type FmConfirmationCopyDescriptor = Omit<FmActionConfirmationDescriptor, 'affectedEntities'>;

export interface FmConfirmProps {
  children: React.ReactElement<TriggerProps>;
  title: React.ReactNode;
  description?: React.ReactNode;
  affectedEntities?: readonly FmConfirmationAffectedEntityDescriptor[];
  confirmText?: React.ReactNode;
  cancelText?: React.ReactNode;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  loading?: boolean;
  danger?: boolean;
  placement?: FmConfirmPlacement;
  getPortalContainer?: () => HTMLElement | null;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  contentTestId?: string;
}

interface FmConfirmSurfaceProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  affectedEntities?: readonly FmConfirmationAffectedEntityDescriptor[];
  confirmText: React.ReactNode;
  cancelText: React.ReactNode;
  danger: boolean;
  loading: boolean;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  contentTestId?: string;
  surfaceRef: React.RefObject<HTMLDialogElement | null>;
  titleId: string;
  descriptionId?: string;
  position: React.CSSProperties;
  onCancel: () => void;
  onConfirm: () => void;
  portalContainer: HTMLElement;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

const OFFSET = 8;
const VIEWPORT_GUTTER = 8;
const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function getFocusable(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

function focusTrigger(triggerRootRef: React.RefObject<HTMLSpanElement | null>): void {
  window.setTimeout(() => {
    const target = triggerRootRef.current?.querySelector<HTMLElement>(
      'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    );
    target?.focus();
  }, 0);
}

function useControllableOpen({
  open,
  defaultOpen,
  onOpenChange,
}: Pick<FmConfirmProps, 'open' | 'defaultOpen' | 'onOpenChange'>): [
  boolean,
  (nextOpen: boolean, restoreFocus?: boolean) => void,
  React.RefObject<boolean>,
] {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false);
  const restoreFocusRef = React.useRef(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? Boolean(open) : internalOpen;

  const setOpen = React.useCallback(
    (nextOpen: boolean, restoreFocus = false) => {
      restoreFocusRef.current = restoreFocus;
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange],
  );

  return [isOpen, setOpen, restoreFocusRef];
}

function useConfirmPosition(args: {
  isOpen: boolean;
  placement: FmConfirmPlacement;
  triggerRootRef: React.RefObject<HTMLSpanElement | null>;
  surfaceRef: React.RefObject<HTMLDialogElement | null>;
}): [React.CSSProperties, () => void] {
  const { isOpen, placement, surfaceRef, triggerRootRef } = args;
  const [position, setPosition] = React.useState<React.CSSProperties>({
    top: 0,
    left: 0,
    visibility: 'hidden',
  });

  const updatePosition = React.useCallback(() => {
    const trigger = triggerRootRef.current;
    const surface = surfaceRef.current;
    if (!trigger || !surface) return;

    const triggerRect = trigger.getBoundingClientRect();
    const surfaceRect = surface.getBoundingClientRect();
    const left = placement.endsWith('Right')
      ? triggerRect.right - surfaceRect.width
      : triggerRect.left;
    const top = placement.startsWith('top')
      ? triggerRect.top - surfaceRect.height - OFFSET
      : triggerRect.bottom + OFFSET;

    setPosition({
      left: clamp(
        left,
        VIEWPORT_GUTTER,
        Math.max(VIEWPORT_GUTTER, window.innerWidth - surfaceRect.width - VIEWPORT_GUTTER),
      ),
      top: clamp(
        top,
        VIEWPORT_GUTTER,
        Math.max(VIEWPORT_GUTTER, window.innerHeight - surfaceRect.height - VIEWPORT_GUTTER),
      ),
      visibility: 'visible',
    });
  }, [placement, surfaceRef, triggerRootRef]);

  React.useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
  }, [isOpen, updatePosition]);

  return [position, updatePosition];
}

function useConfirmLifecycle(args: {
  isOpen: boolean;
  restoreFocusRef: React.RefObject<boolean>;
  setOpen: (nextOpen: boolean, restoreFocus?: boolean) => void;
  surfaceRef: React.RefObject<HTMLDialogElement | null>;
  triggerRootRef: React.RefObject<HTMLSpanElement | null>;
  updatePosition: () => void;
}): void {
  const { isOpen, restoreFocusRef, setOpen, surfaceRef, triggerRootRef, updatePosition } = args;

  React.useEffect(() => {
    if (!isOpen) {
      if (restoreFocusRef.current) {
        restoreFocusRef.current = false;
        focusTrigger(triggerRootRef);
      }
      return;
    }

    const focusFrame = window.requestAnimationFrame(() => {
      getFocusable(surfaceRef.current)[0]?.focus();
    });
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      const insideTrigger = target && triggerRootRef.current?.contains(target);
      const insideSurface = target && surfaceRef.current?.contains(target);
      if (!target || insideTrigger || insideSurface) return;
      setOpen(false, false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false, true);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, restoreFocusRef, setOpen, surfaceRef, triggerRootRef, updatePosition]);
}

function FmConfirmSurface({
  title,
  description,
  affectedEntities,
  confirmText,
  cancelText,
  danger,
  loading,
  className,
  contentTestId,
  surfaceRef,
  titleId,
  descriptionId,
  position,
  onCancel,
  onConfirm,
  portalContainer,
}: FmConfirmSurfaceProps): React.ReactPortal {
  const handleKeyDown: React.KeyboardEventHandler<HTMLDialogElement> = (event) => {
    if (event.key !== 'Tab') return;
    const focusable = getFocusable(surfaceRef.current);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <dialog
      open
      ref={surfaceRef}
      className={cx('fm-confirm__surface', className)}
      aria-modal="false"
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      data-testid={contentTestId}
      style={position}
      onKeyDown={handleKeyDown}
    >
      <div className="fm-confirm__content">
        <h3 className="fm-confirm__title" id={titleId}>
          {title}
        </h3>
        {description ? (
          <div className="fm-confirm__description" id={descriptionId}>
            {description}
          </div>
        ) : null}
        {affectedEntities?.length ? (
          <ul className="fm-confirm__affected" aria-label="Affected items">
            {affectedEntities.map((entity) => (
              <li className="fm-confirm__affected-item" key={entity.id}>
                <span className="fm-confirm__affected-label">{entity.label}</span>
                <span className="fm-confirm__affected-outcome">{entity.outcome}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className="fm-confirm__actions">
        {/* A destructive confirmation promotes its safe choice to a bordered
            button, matching FmModal's destructive tone: a row-level delete and a
            workspace-level delete must not disagree about how the safe choice
            looks. */}
        <FmButton
          variant={danger ? 'bordered' : 'default'}
          size="sm"
          onClick={onCancel}
          disabled={loading}
        >
          {cancelText}
        </FmButton>
        <FmButton variant="primary" size="sm" onClick={onConfirm} loading={loading} danger={danger}>
          {confirmText}
        </FmButton>
      </div>
    </dialog>,
    portalContainer,
  );
}

/** FmConfirm — DS replacement for AntD Popconfirm command confirmations. */
export const FmConfirm: React.FC<FmConfirmProps> = ({
  children,
  title,
  description,
  affectedEntities,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  open,
  defaultOpen,
  onOpenChange,
  disabled = false,
  loading = false,
  danger = false,
  placement = 'topRight',
  getPortalContainer,
  className,
  contentTestId,
}) => {
  const titleId = React.useId();
  const descriptionId = React.useId();
  const triggerRootRef = React.useRef<HTMLSpanElement | null>(null);
  const surfaceRef = React.useRef<HTMLDialogElement | null>(null);
  const overlayPortalRef = React.useContext(FmOverlayPortalContext);
  const [isOpen, setOpen, restoreFocusRef] = useControllableOpen({
    open,
    defaultOpen,
    onOpenChange,
  });
  const [position, updatePosition] = useConfirmPosition({
    isOpen,
    placement,
    triggerRootRef,
    surfaceRef,
  });

  useConfirmLifecycle({
    isOpen,
    restoreFocusRef,
    setOpen,
    surfaceRef,
    triggerRootRef,
    updatePosition,
  });

  const handleCancel = () => {
    onCancel?.();
    setOpen(false, true);
  };
  const handleConfirm = () => {
    void onConfirm();
    setOpen(false, true);
  };
  const handleTriggerClick: React.MouseEventHandler<HTMLElement> = (event) => {
    children.props.onClick?.(event);
    if (event.defaultPrevented || disabled || loading) return;
    setOpen(true, true);
  };

  const trigger = React.cloneElement(children, {
    'aria-haspopup': 'dialog',
    'aria-expanded': isOpen,
    onClick: handleTriggerClick,
  });
  const portalContainer = getPortalContainer?.() ?? overlayPortalRef?.current ?? document.body;

  return (
    <>
      <span ref={triggerRootRef} className="fm-confirm__trigger">
        {trigger}
      </span>
      {isOpen ? (
        <FmConfirmSurface
          title={title}
          description={description}
          affectedEntities={affectedEntities}
          confirmText={confirmText}
          cancelText={cancelText}
          danger={danger}
          loading={loading}
          className={className}
          contentTestId={contentTestId}
          surfaceRef={surfaceRef}
          titleId={titleId}
          descriptionId={descriptionId}
          position={position}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
          portalContainer={portalContainer}
        />
      ) : null}
    </>
  );
};

export default FmConfirm;
